import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ShieldCheck, ShieldX, ShieldAlert, Phone, MapPin, Calendar, Wrench } from "lucide-react";

export default function WarrantyCheckPage() {
  const { ticket } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/repair/${ticket}`).then((r) => setData(r.data)).catch(() => setErr(true));
    document.title = "Verificare garantie service | Troti-X";
  }, [ticket]);

  if (err) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <div className="text-center">
        <ShieldX className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className="font-display text-4xl uppercase">Certificat inexistent</h1>
        <p className="text-neutral-400 mt-3">Verifica numarul de pe certificat sau contacteaza-ne.</p>
        <a href="tel:0743110277" className="inline-block mt-6 bg-[#00E5FF] text-black px-6 py-3 uppercase tracking-wider text-sm font-semibold">Suna 0743 110 277</a>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-white text-center py-24">Se verifica...</div>;

  const hasWarranty = data.warranty_months > 0 && data.warranty_until;
  const expired = hasWarranty && new Date(data.warranty_until) < new Date();
  const valid = hasWarranty && !expired;
  const daysLeft = valid ? Math.max(0, Math.ceil((new Date(data.warranty_until) - new Date()) / 86400000)) : 0;

  return (
    <div data-testid="warranty-check-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-neutral-900 py-5 px-6">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 bg-[#00E5FF] flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="black"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></svg>
          </div>
          <div className="leading-none">
            <div className="font-display text-base">TROTI-X SERVICE</div>
            <div className="text-[9px] tracking-[0.3em] text-[#00E5FF] uppercase mt-1">Verificare garantie</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-3">// Certificat Nr. G-{data.ticket}</div>

        {valid && (
          <div data-testid="warranty-valid" className="border-2 border-green-500 bg-green-950/20 p-8 text-center">
            <ShieldCheck className="w-20 h-20 text-green-400 mx-auto" />
            <h1 className="font-display text-4xl sm:text-5xl uppercase mt-4 text-green-400">Garantie valida</h1>
            <p className="text-neutral-300 mt-3 text-sm">
              Mai ai <strong className="text-white">{daysLeft} {daysLeft === 1 ? "zi" : "zile"}</strong> de garantie · expira pe <strong className="text-white">{new Date(data.warranty_until).toLocaleDateString("ro-RO")}</strong>
            </p>
            <div className="inline-block mt-4 bg-green-500 text-black px-4 py-1.5 text-xs uppercase tracking-widest font-bold">
              {data.warranty_months} {data.warranty_months === 1 ? "luna" : "luni"} garantie
            </div>
          </div>
        )}
        {expired && (
          <div data-testid="warranty-expired" className="border-2 border-red-500 bg-red-950/20 p-8 text-center">
            <ShieldX className="w-20 h-20 text-red-400 mx-auto" />
            <h1 className="font-display text-4xl sm:text-5xl uppercase mt-4 text-red-400">Garantie expirata</h1>
            <p className="text-neutral-300 mt-3 text-sm">
              Garantia de {data.warranty_months} {data.warranty_months === 1 ? "luna" : "luni"} a expirat pe <strong className="text-white">{new Date(data.warranty_until).toLocaleDateString("ro-RO")}</strong>
            </p>
            <p className="text-neutral-500 text-xs mt-2">Te putem ajuta in continuare cu o reparatie noua — suna-ne!</p>
          </div>
        )}
        {!hasWarranty && (
          <div data-testid="warranty-none" className="border-2 border-neutral-700 bg-neutral-950 p-8 text-center">
            <ShieldAlert className="w-20 h-20 text-neutral-500 mx-auto" />
            <h1 className="font-display text-4xl uppercase mt-4 text-neutral-300">Fara garantie inregistrata</h1>
            <p className="text-neutral-500 mt-3 text-sm">Pentru acest ticket nu exista o garantie de service activa.</p>
          </div>
        )}

        <div className="mt-6 bg-neutral-950 border border-neutral-800 p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#00E5FF] font-bold mb-4">
            <Wrench className="w-4 h-4" /> Istoricul lucrarii
          </div>
          <div className="space-y-3 text-sm">
            <InfoRow label="Trotineta" value={data.scooter_model} />
            {data.serial_number && <InfoRow label="Numar serie" value={data.serial_number} />}
            <InfoRow label="Problema raportata" value={data.issue_description} />
            {data.completion_notes && <InfoRow label="Lucrari efectuate" value={data.completion_notes} />}
            {data.warranty_items && <InfoRow label="Acoperit de garantie" value={data.warranty_items} highlight />}
            <InfoRow label="Primita in service" value={new Date(data.created_at).toLocaleDateString("ro-RO")} />
            {data.completed_at && <InfoRow label="Finalizata" value={new Date(data.completed_at).toLocaleDateString("ro-RO")} />}
            {data.final_cost != null && <InfoRow label="Cost lucrare" value={`${data.final_cost} RON`} />}
          </div>
        </div>

        <div className="mt-6 border border-neutral-800 p-6">
          <h3 className="font-display text-lg uppercase mb-4">{valid ? "Ai o problema in garantie?" : "Te ajutam noi"}</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#00E5FF]" /> Bd. Liviu Rebreanu 104A, Timisoara</li>
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[#00E5FF]" /> <a href="tel:0743110277" className="hover:text-[#00E5FF]">0743 110 277</a></li>
            <li className="flex items-start gap-3"><Calendar className="w-4 h-4 mt-0.5 text-[#00E5FF]" /> L-V 9:00-17:00 · Sambata 9:00-14:00</li>
          </ul>
          {valid && <p className="text-xs text-neutral-500 mt-4">Vino cu trotineta si certificatul de garantie — rezolvam cu prioritate.</p>}
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-[#00E5FF]">← Inapoi la site</Link>
        </div>
      </main>
    </div>
  );
}

const InfoRow = ({ label, value, highlight }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 border-b border-neutral-900 pb-2.5">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 sm:w-40 flex-shrink-0">{label}</div>
    <div className={`${highlight ? "text-green-400 font-semibold" : "text-white"}`}>{value}</div>
  </div>
);
